export enum DocumentType {
	INVOICE = "01",
	CREDIT_NOTE = "02",
	DEBIT_NOTE = "03",
	REFUND_NOTE = "04",
	SELF_BILLED_INVOICE = "11",
	SELF_BILLED_CREDIT_NOTE = "12",
	SELF_BILLED_DEBIT_NOTE = "13",
	SELF_BILLED_REFUND_NOTE = "14",
	CONSOLIDATED_INVOICE = "01A",
}

export interface DocumentRequirements {
	referenceDocument: boolean;
	reasonFields: boolean;
	refundFields: boolean;
	supplierApproval: boolean;
}

// Document type specific requirements mapping
export const documentTypeRequirements: Record<
	DocumentType,
	DocumentRequirements
> = {
	[DocumentType.INVOICE]: {
		referenceDocument: false,
		reasonFields: false,
		refundFields: false,
		supplierApproval: false,
	},
	[DocumentType.CREDIT_NOTE]: {
		referenceDocument: true,
		reasonFields: true,
		refundFields: false,
		supplierApproval: false,
	},
	[DocumentType.DEBIT_NOTE]: {
		referenceDocument: true,
		reasonFields: true,
		refundFields: false,
		supplierApproval: false,
	},
	[DocumentType.SELF_BILLED_CREDIT_NOTE]: {
		referenceDocument: true,
		reasonFields: true,
		refundFields: false,
		supplierApproval: false,
	},
	[DocumentType.SELF_BILLED_DEBIT_NOTE]: {
		referenceDocument: true,
		reasonFields: true,
		refundFields: false,
		supplierApproval: false,
	},
	[DocumentType.REFUND_NOTE]: {
		referenceDocument: true,
		reasonFields: false,
		refundFields: true,
		supplierApproval: false,
	},
	[DocumentType.SELF_BILLED_REFUND_NOTE]: {
		referenceDocument: true,
		reasonFields: false,
		refundFields: true,
		supplierApproval: false,
	},
	[DocumentType.SELF_BILLED_INVOICE]: {
		referenceDocument: false,
		reasonFields: false,
		refundFields: false,
		supplierApproval: true,
	},
	[DocumentType.CONSOLIDATED_INVOICE]: {
		referenceDocument: false,
		reasonFields: false,
		refundFields: false,
		supplierApproval: false,
	},
};

// Types for form data
export interface CompanyInfo {
	name: string;
	address: string;
	contact: string;
	email: string;
	tin: string;
	registrationNumber: string;
	sst?: string;
	msicCode?: string;
	businessActivity?: string;
}

export interface BuyerInfo {
	tin: string;
	name: string;
	identificationNumber: string;
	address: string;
	contactNumber: string;
	email: string;
}

export interface LineItem {
	classification: string;
	description: string;
	quantity: number;
	unitPrice: number;
	amount: number;
	disc?: number | null;
	taxRate?: number | null;
	taxAmount?: number | null;
	total: number;
}

export interface ReferenceDocument {
	documentNumber: string;
	documentType: DocumentType;
	documentDate: string;
	originalAmount: number;
}

export interface BankDetails {
	bankName: string;
	accountNumber: string;
	accountHolder: string;
}

export interface DocumentTotals {
	subtotal: number;
	taxAmount: number;
	totalExcludingTax: number;
	totalIncludingTax: number;
	totalPayableAmount: number;
}

export interface BaseDocumentData {
	documentType: DocumentType;
	documentInfo: {
		documentNumber: string;
		documentDate: string;
		uniqueIdentifier: string;
	};
	companyInfo: CompanyInfo;
	buyerInfo: BuyerInfo;
	items: LineItem[];
	totals: DocumentTotals;
	validation: {
		digitalSignature: string;
		validationDateTime: string;
	};
}

export interface DocumentData extends BaseDocumentData {
	referenceDocument?: ReferenceDocument;
	reasonCode?: string;
	reasonDescription?: string;
	refundMethod?: "CASH" | "BANK_TRANSFER" | "CREDIT_CARD";
	bankDetails?: BankDetails;
	supplierApproval?: boolean;
}

export interface ReferenceDocument {
	documentNumber: string;
	documentType: DocumentType;
	documentDate: string;
	originalAmount: number;
}

export enum RefundMethod {
	CASH = "CASH",
	BANK_TRANSFER = "BANK_TRANSFER",
	CREDIT_CARD = "CREDIT_CARD",
}

export type FieldType = "text" | "number" | "select" | "date" | "email" | "tel";

export interface Option {
	value: string;
	label: string;
}

export interface FormFieldConfig {
	name: string;
	label: string;
	type: FieldType;
	placeholder?: string;
	required?: boolean;
	validation?: {
		min?: number;
		max?: number;
		minLength?: number;
		maxLength?: number;
		pattern?: {
			value: RegExp;
			message: string;
		};
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
		custom?: (value: any) => boolean | string;
	};
	options?: Option[];
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	defaultValue?: any;
	gridWidth?: "full" | "half"; // Control field width in grid
}

export interface FormConfig {
	fields: FormFieldConfig[];
	submitLabel?: string;
}
